/**
 * BreadcrumbCollector - Effect-only component that records navigation and click events.
 *
 * This component renders nothing (returns null). It mounts once inside AppContent
 * (the authenticated app shell) and installs two side effects:
 *
 * 1. Route-change listener: whenever the React Router location.pathname changes, a
 *    'nav' breadcrumb is pushed to the sessionStorage ring buffer via pushBreadcrumb().
 *
 * 2. Global click listener: whenever the user clicks a <button> or <a> element
 *    (found via closest()), a 'click' breadcrumb is pushed with the element's label.
 *    Elements inside [data-feedback-capture-exclude] are ignored (e.g. the feedback
 *    button itself to avoid self-referential entries).
 *
 * Both effects clean up on unmount (Risk #5 — listener leak on remount). The
 * click listener is registered document-level once on mount and removed on unmount.
 *
 * @module components/BreadcrumbCollector
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { pushBreadcrumb } from '../services/breadcrumbs';

/**
 * BreadcrumbCollector component.
 *
 * Mount once inside the authenticated app shell (AppContent) to silently record
 * the last 10 navigation/click breadcrumbs for the feedback modal.
 *
 * @returns {null} Renders nothing — effect-only component.
 */
function BreadcrumbCollector() {
  const location = useLocation();

  // Effect 1: push a 'nav' breadcrumb on every route change.
  useEffect(() => {
    pushBreadcrumb({
      t: new Date().toISOString(),
      type: 'nav',
      url: location.pathname,
      label: null,
    });
  }, [location.pathname]);

  // Effect 2: global click listener — record button and anchor clicks.
  useEffect(() => {
    /**
     * Handle a document-level click event.
     *
     * Walks up the DOM from the click target to find the nearest <button> or <a>.
     * Ignores clicks on or inside elements with data-feedback-capture-exclude
     * (used by FeedbackButton to avoid recording its own clicks as breadcrumbs).
     * Ignores clicks on non-interactive elements (divs, spans, etc.).
     *
     * @param {MouseEvent} e - The native click event.
     */
    function handleClick(e) {
      const target = e.target.closest('button, a');
      if (!target) return;
      if (target.closest('[data-feedback-capture-exclude]')) return;

      const label = (
        target.textContent ||
        target.getAttribute('aria-label') ||
        ''
      ).trim().slice(0, 80) || null;

      pushBreadcrumb({
        t: new Date().toISOString(),
        type: 'click',
        url: window.location.pathname,
        label,
      });
    }

    document.addEventListener('click', handleClick);

    // Cleanup: remove the listener on unmount to prevent memory leaks and
    // duplicate entries if the component is ever re-mounted (Risk #5).
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []); // mount-only — no deps

  return null;
}

export default BreadcrumbCollector;
